// 1Rotary — Role-based access control (hardcoded user roster)
// 8 roles, 1Rotary employee-ID format, deterministic 6-char passcodes.
// Visibility matrix encoded per role: which routes are allowed, which KPI
// categories show in the KPI Framework, which Workbench scenario groups,
// and (for Branch Manager) which single branch they are scoped to.

export const USERS = [
  {
    employeeId: "1R-CEO-0001",
    passcode: "WN4ZVU",
    role: "ceo",
    roleLabel: "Chief Executive Officer",
    roleShort: "CEO",
    scope: "Enterprise",
    name: "Atty. Danilo P. Reyes",
    department: "Office of the President",
    accent: "primary",
  },
  {
    employeeId: "1R-COO-0002",
    passcode: "HBSVJS",
    role: "coo",
    roleLabel: "General Manager / COO",
    roleShort: "GM / COO",
    scope: "Enterprise Operations",
    name: "Engr. Maria L. Tan",
    department: "Operations Group",
    accent: "primary",
  },
  {
    employeeId: "1R-VPS-0003",
    passcode: "H9E2QM",
    role: "vp_sales",
    roleLabel: "VP — Sales",
    roleShort: "VP Sales",
    scope: "Sales · Branch & Channel",
    name: "Jeremiah V. Ocampo",
    department: "Sales",
    accent: "primary",
  },
  {
    employeeId: "1R-VPM-0004",
    passcode: "G27E3G",
    role: "vp_merch",
    roleLabel: "VP — Merchandising",
    roleShort: "VP Merch",
    scope: "Product · Supplier · Demand",
    name: "Cherry-Lyn S. Bautista",
    department: "Merchandising",
    accent: "primary",
  },
  {
    employeeId: "1R-CFO-0005",
    passcode: "QSC9BD",
    role: "cfo",
    roleLabel: "Chief Financial Officer",
    roleShort: "CFO",
    scope: "Financial · Treasury · Reporting",
    name: "Ramon B. Aquino, CPA",
    department: "Finance",
    accent: "primary",
  },
  {
    employeeId: "1R-BM-0006",
    passcode: "XLWDLN",
    role: "branch_mgr",
    roleLabel: "Branch Manager — Pasig Ortigas",
    roleShort: "Branch Mgr",
    scope: "Single Branch (Pasig Ortigas)",
    name: "Joselito M. Cruz",
    department: "Retail Operations",
    accent: "primary",
    branchId: "BR-004", // Pasig Ortigas (1Rotary)
    branchName: "Pasig Ortigas",
  },
  {
    employeeId: "1R-VPMK-0007",
    passcode: "U49KX3",
    role: "vp_marketing",
    roleLabel: "VP — Marketing",
    roleShort: "VP Mktg",
    scope: "Channels · Campaigns · Customer",
    name: "Patricia G. Lim",
    department: "Marketing",
    accent: "primary",
  },
  {
    employeeId: "1R-IT-0008",
    passcode: "5UQ7MR",
    role: "it_head",
    roleLabel: "IT Head",
    roleShort: "IT Head",
    scope: "Data Platform · Admin Console",
    name: "Engr. Mark D. Salonga",
    department: "Information Technology",
    accent: "primary",
  },
  {
    employeeId: "1R-PILOT-0009",
    passcode: "BMY8B7",
    role: "sow_pilot",
    roleLabel: "Lite Pilot — Inventory & Demand",
    roleShort: "Pilot Lead",
    scope: "SOW: Inventory Intelligence + Demand Forecast",
    name: "Ma. Corazon T. Rivera",
    department: "Demand Planning · Pilot Program",
    accent: "primary",
  },
];

// ---------------------------------------------------------------------------
// Visibility matrix — keyed by role id, values are allowed route paths.
// "/twin" includes the Digital Twin Scenario Library; "/workbench" the
// Simulation Workbench. Admin Console routes are nested under "/admin/*".
// ---------------------------------------------------------------------------
export const ROUTE_ACCESS = {
  ceo: ["/", "/morning-brief", "/branches", "/inventory", "/procurement", "/omnichannel", "/merchandising", "/financial", "/reports", "/forecast", "/anomalies", "/operations", "/twin", "/workbench", "/kpi", "/admin/pipeline", "/admin/explorer", "/admin/data-management", "/admin/engines", "/admin/classification", "/admin/cost-transparency", "/admin/api-keys"],
  coo: ["/", "/morning-brief", "/branches", "/inventory", "/procurement", "/omnichannel", "/merchandising", "/financial", "/reports", "/forecast", "/anomalies", "/operations", "/twin", "/workbench", "/kpi", "/admin/explorer", "/admin/data-management", "/admin/classification"],
  vp_sales: ["/", "/morning-brief", "/branches", "/omnichannel", "/anomalies", "/workbench", "/kpi", "/reports"],
  vp_merch: ["/", "/morning-brief", "/inventory", "/procurement", "/omnichannel", "/merchandising", "/forecast", "/anomalies", "/operations", "/workbench", "/kpi"],
  cfo: ["/", "/morning-brief", "/branches", "/inventory", "/procurement", "/financial", "/reports", "/forecast", "/anomalies", "/workbench", "/kpi", "/admin/cost-transparency"],
  branch_mgr: ["/", "/morning-brief", "/branches", "/inventory", "/omnichannel", "/anomalies", "/operations", "/financial", "/kpi"],
  vp_marketing: ["/", "/omnichannel", "/merchandising", "/workbench", "/kpi"],
  it_head: ["/", "/morning-brief", "/anomalies", "/admin/pipeline", "/admin/explorer", "/admin/data-management", "/admin/engines", "/admin/classification", "/admin/cost-transparency", "/admin/api-keys", "/admin/modules", "/kpi"],
  // SOW Lite Pilot — scoped strictly to SOW deliverables (D1..D6):
  //   D1 → /admin/* (data readiness & integration)
  //   D2 → /inventory  D3 → /forecast + /operations
  //   D4 → /twin + /workbench (stockout/overstock only)
  //   D5 → /reports  · plus / (landing) and /kpi (SOW KPIs only)
  sow_pilot: ["/", "/morning-brief", "/inventory", "/forecast", "/operations", "/twin", "/workbench", "/kpi", "/reports", "/admin/pipeline", "/admin/explorer", "/admin/data-management"],
};

// Branch detail uses dynamic param /branches/:id — fold into /branches gate.
export function canAccessRoute(role, path) {
  if (!role) return false;
  const allow = ROUTE_ACCESS[role] || [];
  // exact match
  if (allow.includes(path)) return true;
  // dynamic /branches/:id
  if (path.startsWith("/branches/") && allow.includes("/branches")) return true;
  // Forecast / Prediction Models sub-pages share the /forecast gate
  if (path.startsWith("/forecast/") && allow.includes("/forecast")) return true;
  // admin sub-paths
  if (path.startsWith("/admin/") && allow.includes(path)) return true;
  return false;
}

// ---------------------------------------------------------------------------
// KPI Framework — which categories each role sees.
// Categories present in KPI_FRAMEWORK (data.js): "Inventory", "Demand", "Branch",
// "Channel", "Supplier", "Financial", "E-Commerce".
// ---------------------------------------------------------------------------
export const KPI_ACCESS = {
  ceo: null, // null = all
  coo: null,
  vp_sales: ["Revenue", "Branch Ops", "E-Commerce"],
  vp_merch: ["Inventory", "Forecasting", "Supply Chain"],
  cfo: ["Financial", "Supply Chain"],
  branch_mgr: ["Branch Ops", "Inventory"],
  vp_marketing: ["E-Commerce", "Revenue"],
  it_head: ["Inventory", "Forecasting", "Supply Chain"], // data-quality lens
  // SOW pilot: only KPIs the pilot dashboards produce
  sow_pilot: ["Inventory", "Forecasting", "Supply Chain"],
};

// ---------------------------------------------------------------------------
// Simulation Workbench — which scenario groups each role can run.
// Groups present in Workbench: "Operations & Demand", "Purchasing & Lead Time".
// We extend semantically: marketing/sales roles see all but scoped narratives.
// ---------------------------------------------------------------------------
export const WORKBENCH_ACCESS = {
  ceo: null,
  coo: null,
  vp_sales: ["Operations & Demand"],
  vp_merch: ["Operations & Demand", "Purchasing & Lead Time"],
  cfo: ["Purchasing & Lead Time"],
  branch_mgr: [],
  vp_marketing: ["Operations & Demand"],
  it_head: [],
  // SOW pilot: D4 Stockout & Overstock Scenario Simulator — both groups relevant
  sow_pilot: ["Operations & Demand", "Purchasing & Lead Time"],
};

// Extra: for the SOW pilot, only 3 scenarios are relevant per SOW D4:
//   summer-surge (pre-season demand multiplier)
//   supply-disruption (Taiwan supplier 3-week delay what-if)
//   sku-rationalize (dead-stock rationalisation)
export const WORKBENCH_SCENARIO_ALLOW = {
  sow_pilot: ["summer-surge", "supply-disruption", "sku-rationalize", "po-lead-time", "moq-optimization"],
};

// ---------------------------------------------------------------------------
// Digital Twin scenario library — category access
// ---------------------------------------------------------------------------
export const TWIN_ACCESS = {
  ceo: null,
  coo: null,
  vp_sales: ["Channel", "Demand", "Competitive"],
  vp_merch: ["Supply Chain", "Demand", "Product"],
  cfo: ["Macro", "Cost"],
  branch_mgr: [],
  vp_marketing: ["Channel", "Climate"],
  it_head: [],
  // SOW pilot: only Stockout/Overstock relevant scenario categories
  sow_pilot: ["Supply Chain", "Demand", "Climate"],
};

// SOW Reports Center scoping — 3 SOW-specific reports only
export const REPORT_ACCESS = {
  ceo: null, coo: null, vp_sales: null, vp_merch: null, cfo: null,
  branch_mgr: null, vp_marketing: null, it_head: null,
  // Pilot sees ONLY the three SOW D5 reports:
  sow_pilot: ["RPT-BRANCH-INV-SUM", "RPT-WEEKLY-REORDER", "RPT-DEADSTOCK-CAND"],
};

// ---------------------------------------------------------------------------
// Lookup helpers
// ---------------------------------------------------------------------------
export function findUserByCredentials(employeeId, passcode) {
  if (!employeeId || !passcode) return null;
  const eid = employeeId.trim().toUpperCase();
  const pc = passcode.trim().toUpperCase();
  return USERS.find(u => u.employeeId.toUpperCase() === eid && u.passcode.toUpperCase() === pc) || null;
}
export function getUserByRole(role) {
  return USERS.find(u => u.role === role) || null;
}
